import React, { FunctionComponent, ReactNode, useMemo } from 'react';
import { InvestmentsApi, InvestmentsListOrderingEnum, InvestmentsValuesRetrieveFieldEnum } from 'api/apis';
import { InvestmentDetails, Money } from 'api/models';
import WithDataRecord from 'hoc/WithDataRecord';
import useGoTo from 'hooks/use-go-to';
import { INVESTOR_ROUTES } from 'apps/investor/pages/routes.config';
import { InvestmentsListStatusEnum } from 'api';
import cx from 'ui/helper/prefixed-class-names';
import Translate from 'ui/atoms/translate';
import Currency from 'ui/atoms/currency';
import Date from 'ui/atoms/date';
import PlaceholderFallback from 'ui/atoms/placeholder-fallback';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import { DataRecordOrdering } from 'ui/types/data-ordering';
import { HorizontalAlign } from 'ui/types/align';
import { toNumber } from 'ui/helper/money';
import { PaginationProps } from 'ui/molecules/pagination';
import useApiCall from 'src/hooks/use-api-call';
import { Search } from 'src/ui/molecules/filtering';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';

interface Investment {
  id: string;
  investmentTotal: Money;
  investmentTotalFulfilled: Money;
  productName: string;
  sumSettledPayouts: Money;
  lastSettledPayout: Money | null;
  nextScheduledPayout: Date | null;
  signedAt: Date;
}

export interface DashboardInvestmentsTableProps {
  /** Primary content. */
  children?: ReactNode;
  /** Additional classes. */
  className?: string;
  title?: ReactNode;
  showProductColumn?: boolean;
  filters?: ReactNode;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  goToDetails: (investmentId: string) => void;
  loading?: boolean;
  paginationProps?: PaginationProps;
  investments?: InvestmentDetails[];
  hidePaginationNavigation?: boolean;
  status?: string;
  tokenId?: string;
}

const DashboardInvestmentsTable: FunctionComponent<DashboardInvestmentsTableProps> = (props) => {
  const {
    className,
    children,
    title,
    onOrderBy,
    ordering,
    goToDetails,
    loading,
    paginationProps,
    showProductColumn,
    investments,
    hidePaginationNavigation,
    tokenId,
    status,
    ...restProps
  } = props;

  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const investmentsApi: InvestmentsApi = useMemo(() => makeAuthenticatedApi(InvestmentsApi), [makeAuthenticatedApi]);

  return (
    <div className={cx('dashboard-investments-table', className)} {...restProps}>
      <DataRecordTable
        title={title}
        onOrderBy={onOrderBy}
        ordering={ordering}
        data={investments}
        loading={loading}
        paginationProps={paginationProps}
        onGoToDetails={goToDetails}
        hidePaginationNavigation={hidePaginationNavigation}
        searchComponent={
          <Search input="search" defaultField="search" label={translate('filters.labels.investments')} name="search" />
        }
        cells={[
          ...(showProductColumn
            ? [
                {
                  title: <Translate name="dashboardInvestorInvestments.product" />,
                  render: (investment: Investment) => investment.productName,
                  orderingFieldName: InvestmentsListOrderingEnum.PRODUCT_NAME_ASC,
                  filterName: 'productName',
                  searchFieldName: InvestmentsValuesRetrieveFieldEnum.PRODUCT_NAME,
                  getFilterData: (params: any) =>
                    investmentsApi.investmentsValuesRetrieve({ ...params, status: status, productId: tokenId }),
                },
              ]
            : []),
          {
            title: <Translate name="dashboardInvestorInvestments.signedAt" />,
            render: (investment: Investment) => investment.signedAt && <Date>{investment.signedAt}</Date>,
            alignContent: 'right' as HorizontalAlign,
            orderingFieldName: InvestmentsListOrderingEnum.SIGNED_AT_ASC,
            type: CellTypeEnum.date,
            filterName: 'signedAt',
          },
          {
            title: <Translate name="dashboardInvestorInvestments.currentInvestment" />,
            render: (investment) => (
              <PlaceholderFallback>
                {investment?.investmentTotal && <Currency>{investment?.investmentTotal}</Currency>}
              </PlaceholderFallback>
            ),
            alignContent: 'right',
            orderingFieldName: InvestmentsListOrderingEnum.INVESTMENT_TOTAL_ASC,
            type: CellTypeEnum.currency,
            filterName: 'investmentTotal',
          },
          {
            title: <Translate name="dashboardInvestorInvestments.initialInvestment" />,
            render: (investment) => (
              <PlaceholderFallback>
                {investment?.investmentTotalFulfilled && <Currency>{investment?.investmentTotalFulfilled}</Currency>}
              </PlaceholderFallback>
            ),
            alignContent: 'right',
            orderingFieldName: InvestmentsListOrderingEnum.INVESTMENT_TOTAL_FULFILLED_ASC,
            type: CellTypeEnum.currency,
            filterName: 'investmentTotalFulfilled',
          },
          {
            title: <Translate name="dashboardInvestorInvestments.sumSettledPayouts" args={[<>&shy;</>]} />,
            render: (investment) => <Currency>{investment.sumSettledPayouts}</Currency>,
            alignContent: 'right',
            orderingFieldName: InvestmentsListOrderingEnum.SUM_SETTLED_PAYOUTS_ASC,
            type: CellTypeEnum.currency,
            filterName: 'sumSettledPayouts',
          },
          {
            title: <Translate name="dashboardInvestorInvestments.lastSettledPayout" />,
            render: (investment) =>
              investment.lastSettledPayout &&
              toNumber(investment.lastSettledPayout) > 0 && <Currency>{investment.lastSettledPayout}</Currency>,
            alignContent: 'right',
            orderingFieldName: InvestmentsListOrderingEnum.LAST_SETTLED_PAYOUT_ASC,
            type: CellTypeEnum.currency,
            filterName: 'lastSettledPayoutAmount',
          },
          {
            title: <Translate name="dashboardInvestorInvestments.nextScheduledPayout" />,
            render: (investment) => investment.nextScheduledPayout && <Date>{investment.nextScheduledPayout}</Date>,
            alignContent: 'right',
            orderingFieldName: InvestmentsListOrderingEnum.NEXT_SCHEDULED_PAYOUT_ASC,
            type: CellTypeEnum.date,
            filterName: 'nextScheduledPayout',
          },
        ]}
      />
    </div>
  );
};

interface InvestorInvestmentsTableViewProps {
  title?: ReactNode;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  showProductColumn?: boolean;
  defaultLimit?: number;
  skipFilterUrlSync?: boolean;
  status?: string;
  token?: string;
  availableLimits?: number[];
  hidePaginationNavigation?: boolean;
}

const InvestorInvestmentsTableView: FunctionComponent<InvestorInvestmentsTableViewProps> = WithDataRecord<
  InvestmentsApi,
  InvestmentDetails
>(InvestmentsApi)(
  ({
    data,
    ordering,
    onOrderBy,
    title,
    loading,
    paginationProps,
    showProductColumn,
    hidePaginationNavigation,
    status,
    token,
  }) => {
    const goToInvestment = useGoTo(INVESTOR_ROUTES.investment);

    return (
      <DashboardInvestmentsTable
        title={title}
        onOrderBy={onOrderBy}
        ordering={ordering}
        investments={data}
        loading={loading}
        goToDetails={(investmentId: string) => goToInvestment({ id: investmentId })}
        paginationProps={paginationProps}
        showProductColumn={showProductColumn}
        hidePaginationNavigation={hidePaginationNavigation}
        status={status}
        tokenId={token}
      />
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.investmentsList({
      offset,
      limit,
      ...props,
      ordering: ordering as InvestmentsListOrderingEnum,
      status: props.status as InvestmentsListStatusEnum,
      productId: props.token ? [props.token] : undefined,
    });
  },
);

export default InvestorInvestmentsTableView;
